// E-2 — blockArt.ts: figure-art silhouettes per tower type (§5.2.3a).
// Pure path builders — return SVG path strings / marker specs, no React.
import type { StructureType } from '@/types/planData';

export interface BlockArt {
  /** outline path(s) in local px coordinates, origin at the block node */
  paths: string[];
  /** block rendered as a small square of this half-size */
  half: number;
}

export function blockArt(kind: string, half = 4): BlockArt {
  switch (kind) {
    case 'topBlock':
    case 'heelBlock':
    case 'jumpBlock':
    case 'redirectBlock':
    case 'mountBlock':
      return {
        half,
        paths: [
          `M ${-half} ${-half} H ${half} V ${half} H ${-half} Z`,                 // block shell
          `M ${half * 0.55} 0 A ${half * 0.55} ${half * 0.55} 0 1 1 ${-half * 0.55} 0 A ${half * 0.55} ${half * 0.55} 0 1 1 ${half * 0.55} 0`, // visible sheave circle
          `M 0 ${-half - 4} V ${-half}`,                                          // shackle pin
          `M ${-2.5} ${-half - 4} A 2.5 2.5 0 1 1 2.5 ${-half - 4}`,              // shackle bow
          `M ${-half - 3} ${-half - 7} L ${-half} ${-half - 4} M ${half + 3} ${-half - 7} L ${half} ${-half - 4}`, // sling legs to tower
        ],
      };
    case 'winch':
      return {
        half,
        paths: [
          // truck body + cab (reference-styled truck-mounted winch)
          `M ${-half * 3} ${half * 1.5} H ${half * 2} V ${-half * 0.5} H ${-half * 3} Z`,  // bed
          `M ${half * 2} ${half * 1.5} V ${-half * 1.5} H ${half * 3} L ${half * 3.5} ${half * 1.5}`, // cab
          `M ${-half * 2.2} ${half * 1.5} A ${half * 0.7} ${half * 0.7} 0 1 1 ${-half * 0.8} ${half * 1.5}`, // wheel 1
          `M ${half * 1} ${half * 1.5} A ${half * 0.7} ${half * 0.7} 0 1 1 ${half * 2.4} ${half * 1.5}`,       // wheel 2
          `M ${-half * 2} ${-half * 0.5} A ${half * 0.9} ${half * 0.9} 0 1 1 ${-half * 0.2} ${-half * 0.5}`,   // winch drum
          `M ${-half * 1.1} ${-half * 0.5} A ${half * 0.35} ${half * 0.35} 0 1 1 ${-half * 0.4} ${-half * 0.5}`, // drum hub
        ],
      };
    case 'load':
      return {
        half,
        paths: [
          `M ${-half * 1.5} ${-half} H ${half * 1.5} V ${half * 1.5} H ${-half * 1.5} Z`,
        ],
      };
    case 'tagAnchor':
    case 'tieBackAnchor':
    case 'staticUpperAnchor':
    case 'highlineShaftAnchor':
      return { half: half * 0.75, paths: [`M ${-half} ${half} L 0 ${-half} L ${half} ${half} Z`] }; // ground/anchor wedge
    case 'trolley':
      return {
        half,
        paths: [
          `M ${-half} ${-half * 0.5} H ${half} V ${half * 0.5} H ${-half} Z`,     // trolley body riding the line
          `M ${half * 0.45} 0 A ${half * 0.45} ${half * 0.45} 0 1 1 ${-half * 0.45} 0 A ${half * 0.45} ${half * 0.45} 0 1 1 ${half * 0.45} 0`, // sheave
          `M 0 ${half * 0.5} V ${half * 1.2}`,                                    // hang-down to the load
        ],
      };
    default:
      return { half, paths: [`M ${-half} ${-half} H ${half} V ${half} H ${-half} Z`] };
  }
}

// --- tower silhouettes (§5.1 item 3): type governs available attachments -----

export interface TowerSilhouette {
  /** outline + X-brace paths in SCENE FT, tower centered at x=0 from grade 0 to heightFt */
  paths: string[];
  /** attachment methods available for this tower type (§5.2) */
  attachments: string[];
  guyLevels?: { yFt: number; xFt: number }[];
}

export function towerSilhouette(type: StructureType, heightFt: number, faceWidthFt: number): TowerSilhouette {
  const halfW = faceWidthFt / 2;
  const paths: string[] = [];
  // Reference-styled art (Brian 2026-09-08): tapered lattice legs with X-bracing
  // and horizontal panel-point members, base plates at grade, top platform with
  // antenna mast + mounts. TRUE-SCALE — no width fudge factors; the face-width
  // input drives the taper.
  const panel = Math.max(10, Math.min(25, heightFt / 8)); // panel spacing in ft
  switch (type) {
    case 'monopole': {
      // slightly tapered shaft + base flange + top cap
      const botW = halfW;
      const topW = halfW * 0.7;
      paths.push(`M ${-botW} 0 L ${-topW} ${heightFt} L ${topW} ${heightFt} L ${botW} 0 Z`);
      paths.push(`M ${-botW * 1.6} 0 H ${botW * 1.6}`); // base flange
      paths.push(`M ${-topW * 1.3} ${heightFt} H ${topW * 1.3}`); // top cap
      return { paths, attachments: ['poleShaft', 'antennaMount'] };
    }
    case 'mast-pipe': {
      const w = halfW * 0.4;
      paths.push(`M ${-w} 0 H ${w} V ${heightFt} H ${-w} Z`);
      paths.push(`M ${-w * 2} 0 H ${w * 2}`);
      return { paths, attachments: ['mastPipeRoosterHead', 'poleShaft'] };
    }
    case 'guyed': {
      // tapering shaft, panel horizontals, guys from attachment to ground anchors
      const topHalf = halfW * 0.35;
      paths.push(`M ${-halfW} 0 L ${-topHalf} ${heightFt} L ${topHalf} ${heightFt} L ${halfW} 0 Z`);
      const guys: { yFt: number; xFt: number }[] = [];
      for (let y = 50; y < heightFt; y += 50) {
        const f = y / heightFt;
        const xAt = halfW + (topHalf - halfW) * f;
        guys.push({ yFt: y, xFt: xAt });
        paths.push(`M ${-xAt} ${y} H ${xAt}`); // panel horizontal
        const anchorX = xAt + y * 0.6; // guy anchor spread ≈ 60% of attachment height
        paths.push(`M ${-xAt} ${y} L ${-anchorX} 0 M ${xAt} ${y} L ${anchorX} 0`);
        paths.push(`M ${-anchorX - 2} 0 L ${-anchorX + 2} -3 M ${anchorX - 2} 0 L ${anchorX + 2} -3`); // anchor stake
      }
      paths.push(`M ${-halfW * 1.5} 0 H ${halfW * 1.5}`); // base plate
      return { paths, attachments: ['legPanelPoint', 'poleShaft', 'antennaMount'], guyLevels: guys };
    }
    case 'self-support':
    default: {
      // tapered legs + X-bracing + panel horizontals + base plates + top platform
      const botHalf = halfW * 3; // realistic lattice base spread
      const topHalf = halfW;
      paths.push(`M ${-botHalf} 0 L ${-topHalf} ${heightFt} L ${topHalf} ${heightFt} L ${botHalf} 0 Z`);
      for (let y = 0; y < heightFt; y += panel) {
        const f0 = y / heightFt;
        const y1 = Math.min(y + panel, heightFt);
        const f1 = y1 / heightFt;
        const x0 = botHalf + (topHalf - botHalf) * f0;
        const x1 = botHalf + (topHalf - botHalf) * f1;
        paths.push(`M ${-x0} ${y} L ${x1} ${y1} M ${x0} ${y} L ${-x1} ${y1}`); // X-brace
        paths.push(`M ${-x1} ${y1} H ${x1}`); // panel-point horizontal
      }
      // base plates at grade
      paths.push(`M ${-botHalf - 4} 0 H ${-botHalf + 4} M ${botHalf - 4} 0 H ${botHalf + 4}`);
      // top platform + antenna mast + side mounts (reference-picture styling)
      const pw = topHalf * 2.6;
      paths.push(`M ${-pw} ${heightFt} H ${pw}`);
      paths.push(`M ${-pw} ${heightFt} V ${heightFt - 2} M ${pw} ${heightFt} V ${heightFt - 2}`);
      paths.push(`M 0 ${heightFt} V ${heightFt + Math.max(8, heightFt * 0.06)}`); // antenna mast
      const my = heightFt + Math.max(8, heightFt * 0.06);
      paths.push(`M ${-2} ${my} H ${2}`); // mast cap
      paths.push(`M ${-pw * 0.7} ${heightFt} V ${heightFt - 4} M ${pw * 0.7} ${heightFt} V ${heightFt - 4}`); // side mounts
      return { paths, attachments: ['legPanelPoint', 'poleShaft', 'antennaMount', 'faceBasket2'] };
    }
  }
}
